import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { MetricCard } from '../components/ui/metric-card';
import { useColumnConfig, ColumnConfigPopover } from '../components/ColumnConfig';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import { apiFetch } from '../utils/api';
import ReviewTicker from '../components/ReviewTicker';
import CustomerSupportPage from './CustomerSupportPage';
import {
  Star,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  AlertTriangle,
  Heart,
  Store,
  Trophy,
  AlertCircle,
  Activity,
  Package,
  ArrowLeft,
  Search,
  Clock,
  Brain,
  Truck,
  Lightbulb,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Sparkles,
  Hash,
  ThumbsUp,
  ThumbsDown,
  BarChart3,
  Loader2,
  Eye,
  PlayCircle,
} from 'lucide-react';

const RATING_COLORS = { 1: '#ef4444', 2: '#f97316', 3: '#f59e0b', 4: '#84cc16', 5: '#2eb4ac' };

// Expandable text component
const ExpandableText = ({ text, maxLength = 150 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  if (text.length <= maxLength) return <span>{text}</span>;
  
  return (
    <span>
      {expanded ? text : `${text.slice(0, maxLength)}...`}
      <button 
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        className="ml-1 text-[#3b82f6] hover:underline text-xs font-medium inline-flex items-center"
      >
        {expanded ? <><ChevronUp className="w-3 h-3" /> Less</> : <><ChevronDown className="w-3 h-3" /> More</>}
      </button>
    </span>
  );
};

// Outer wrapper — routes "Customer Support" channel to the call-logs view,
// everything else falls through to the reviews-based ChannelDashboard.
export default function ChannelDashboardRouter() {
  const { brandName, platformName } = useParams();
  if (decodeURIComponent(platformName || '') === 'Customer Support') {
    return <CustomerSupportPage brand={decodeURIComponent(brandName || '')} />;
  }
  return <ReviewsChannelDashboardPage />;
}

function ReviewsChannelDashboardPage() {
  const { brandName, platformName } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platformIcon, setPlatformIcon] = useState(null);
  const [channelRating, setChannelRating] = useState(null);
  const [brandLogo, setBrandLogo] = useState(null);
  const [skuUrls, setSkuUrls] = useState([]);
  const [manualRatings, setManualRatings] = useState([]);
  const [metaPosts, setMetaPosts] = useState([]);
  const [metaComments, setMetaComments] = useState({});
  const [metaAnalytics, setMetaAnalytics] = useState(null);
  const [analyzingMeta, setAnalyzingMeta] = useState(false);
  const [loadingMetaAnalytics, setLoadingMetaAnalytics] = useState(false);
  const [refreshingStatsId, setRefreshingStatsId] = useState(null);

  // Compact number formatter (1.2k, 63.5k, 1.2M)
  const compactNumber = (n) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return '0';
    const num = Number(n);
    if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(num % 1e6 === 0 ? 0 : 1) + 'M';
    if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(num % 1e3 === 0 ? 0 : 1) + 'k';
    return String(Math.round(num));
  };
  const [fetchingSkuRatings, setFetchingSkuRatings] = useState(false);
  const [lastSkuFetchedAt, setLastSkuFetchedAt] = useState(null);
  const [productImages, setProductImages] = useState({});

  const defaultChannelSkuCols = useMemo(() => [
    { id: 'product', label: 'Product / SKU', visible: true },
    { id: 'live_rating', label: 'Live Rating', visible: true },
    { id: 'live_reviews', label: 'Live No. of Ratings/Reviews', visible: true },
    { id: 'analysed_reviews', label: 'Analysed Reviews', visible: true },
    { id: 'analysed_rating', label: 'Analysed Rating', visible: true },
    { id: 'sentiment', label: 'Sentiment', visible: true },
  ], []);
  const { columns: channelSkuCols, setColumns: setChannelSkuCols, visibleColumns: visChannelSkuCols } = useColumnConfig('channel_sku_columns', defaultChannelSkuCols);

  useEffect(() => {
    if (brandName && platformName) {
      fetchChannelData();
      fetchPlatformIcon();
      fetchChannelRating();
      fetchBrandLogo();
      fetchSkuUrls();
      fetchProductImages();
      if (['Blinkit', 'Zepto'].includes(platformName)) {
        fetchManualRatings();
      }
      if (platformName === 'Meta') {
        fetchMetaPosts();
        fetchMetaAnalytics();
      }
    }
  }, [brandName, platformName]);

  const fetchBrandLogo = async () => {
    try {
      const res = await apiFetch('/api/brands', { credentials: 'include' });
      if (res.ok) {
        const bl = await res.json();
        const match = bl.find(b => b.name === brandName);
        if (match?.logo_url) setBrandLogo(match.logo_url);
      }
    } catch (err) { console.error(err); }
  };

  const fetchChannelRating = async () => {
    try {
      const res = await apiFetch(`/api/scraper/brand-summary/${encodeURIComponent(brandName)}`);
      if (res.ok) {
        const result = await res.json();
        const platforms = result.platforms || [];
        const match = platforms.find(p => p.platform === platformName);
        if (match?.avg_rating) {
          setChannelRating({ avg_rating: match.avg_rating, total_reviews: match.total_reviews, isLive: true });
        }
      }
    } catch (err) { console.error(err); }
  };

  const fetchPlatformIcon = async () => {
    try {
      const res = await apiFetch('/api/platforms', { credentials: 'include' });
      if (res.ok) {
        const pl = await res.json();
        const match = pl.find(p => p.name === platformName);
        if (match?.icon_url) setPlatformIcon(match.icon_url);
      }
    } catch (err) { console.error(err); }
  };

  const fetchChannelData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/dashboard/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(platformName)}`);
      if (response.ok) {
        setData(await response.json());
      } else if (response.status === 404) {
        // No reviews yet — show empty state (Blinkit/Zepto may only have manual ratings)
        setData({
          brand_name: brandName,
          platform_name: platformName,
          total_reviews: 0,
          sku_count: 0,
          avg_rating: 0,
          health_score: 0,
          sentiment_distribution: {},
          sku_performance: [],
          top_skus: [],
          underperforming_skus: [],
          review_trend: [],
          ai_insight: `No uploaded reviews for ${brandName} on ${platformName} yet. Use the Control Panel to manage tracked URLs and manual ratings.`,
        });
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to load channel data');
      }
    } catch (err) {
      console.error('Error fetching channel data:', err);
      setError('Failed to load channel data');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to reviews with filter

  // === SKU URL Tracking Functions ===
  const fetchSkuUrls = async () => {
    try {
      const res = await apiFetch(`/api/scraper/sku-urls?brand=${encodeURIComponent(brandName)}&platform=${encodeURIComponent(platformName)}`);
      if (res.ok) setSkuUrls(await res.json());
    } catch (err) { console.error('Failed to fetch SKU URLs:', err); }
  };

  const fetchProductImages = async () => {
    try {
      const res = await apiFetch('/api/products/grouped');
      if (res.ok) {
        const grouped = await res.json();
        const imgMap = {};
        (grouped.brands || []).forEach(b => {
          b.products.forEach(p => { if (p.image_url) imgMap[p.sku_name] = p.image_url; });
        });
        setProductImages(imgMap);
      }
    } catch (err) { console.error(err); }
  };

  const fetchManualRatings = async () => {
    try {
      const res = await apiFetch('/api/manual-ratings');
      if (res.ok) {
        const all = await res.json();
        const filtered = all.filter(r =>
          r.brand?.toLowerCase() === brandName?.toLowerCase() &&
          r.platform?.toLowerCase() === platformName?.toLowerCase()
        );
        setManualRatings(filtered);
      }
    } catch (err) { console.error(err); }
  };

  const fetchMetaPosts = async () => {
    try {
      const res = await apiFetch(`/api/meta/posts?brand=${encodeURIComponent(brandName)}`);
      if (res.ok) {
        const posts = await res.json();
        setMetaPosts(posts);
        // Fetch comments for each post
        for (const post of posts) {
          const cRes = await apiFetch(`/api/meta/posts/${post.post_id}/comments`);
          if (cRes.ok) {
            const data = await cRes.json();
            setMetaComments(prev => ({ ...prev, [post.post_id]: data.comments }));
          }
        }
      }
    } catch (err) { console.error(err); }
  };

  const fetchMetaAnalytics = async () => {
    setLoadingMetaAnalytics(true);
    try {
      const res = await apiFetch(`/api/meta/analytics?brand=${encodeURIComponent(brandName)}`);
      if (res.ok) {
        setMetaAnalytics(await res.json());
      }
    } catch (err) { console.error(err); }
    setLoadingMetaAnalytics(false);
  };

  const refreshMetaPostStats = async (postId) => {
    setRefreshingStatsId(postId);
    try {
      const res = await apiFetch(`/api/meta/posts/${postId}/refresh-stats`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setMetaPosts(prev => prev.map(p => p.post_id === postId ? { ...p, ...data } : p));
      }
    } catch (err) { console.error(err); }
    setRefreshingStatsId(null);
  };

  const analyzeMetaComments = async () => {
    setAnalyzingMeta(true);
    try {
      // Loop until everything is analyzed (batches of 100). Hard cap of 50 iterations as safety.
      for (let i = 0; i < 50; i++) {
        const res = await apiFetch(`/api/meta/analyze?brand=${encodeURIComponent(brandName)}&batch_size=100`, { method: 'POST' });
        if (!res.ok) break;
        const data = await res.json();
        if (!data.total_remaining || data.total_remaining <= 0 || data.count === 0) break;
      }
      await fetchMetaAnalytics();
      await fetchMetaPosts();
    } catch (err) { console.error(err); }
    setAnalyzingMeta(false);
  };

  const fetchAllSkuRatings = async () => {
    setFetchingSkuRatings(true);
    try {
      await apiFetch(`/api/scraper/sku-fetch-all?brand=${encodeURIComponent(brandName)}&platform=${encodeURIComponent(platformName)}`, { method: 'POST' });
      setLastSkuFetchedAt(new Date().toISOString());
      await fetchSkuUrls();
    } catch (err) { console.error(err); }
    setFetchingSkuRatings(false);
  };

  const navigateToReviews = (filters) => {
    const params = new URLSearchParams();
    if (filters.keyword) params.set('keyword', filters.keyword);
    if (filters.topic) params.set('topic', filters.topic);
    if (filters.highlight) params.set('highlight', filters.highlight);
    if (brandName) params.set('brand', brandName);
    if (platformName) params.set('platform', platformName);
    if (filters.sentiment) params.set('sentiment', filters.sentiment);
    navigate(`/reviews?${params.toString()}`);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm mb-2">{label}</p>
          {payload.map((entry) => (
            <p key={entry.name} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate metrics
  const positiveCount = data?.sentiment_distribution?.positive || 0;
  const negativeCount = data?.sentiment_distribution?.negative || 0;
  const neutralCount = data?.sentiment_distribution?.neutral || 0;
  const totalSentiment = positiveCount + negativeCount + neutralCount;
  const positivePercent = totalSentiment > 0 ? Math.round((positiveCount / totalSentiment) * 100) : 0;
  const negativePercent = totalSentiment > 0 ? Math.round((negativeCount / totalSentiment) * 100) : 0;

  // Ranking: Channel Rating desc → Reviews desc → SKU Rating desc
  const sortedSkus = [...(data?.sku_performance || [])].sort((a, b) => {
    if (b.total_reviews !== a.total_reviews) return b.total_reviews - a.total_reviews;
    return (b.avg_rating || 0) - (a.avg_rating || 0);
  });
  const rankedTopSkus = sortedSkus.filter(s => s.total_reviews >= 3).slice(0, 5);
  const rankedUnderperformingSkus = [...sortedSkus.filter(s => s.total_reviews >= 2)].reverse().slice(0, 5);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <AlertCircle className="w-16 h-16 text-destructive" />
        <h2 className="text-xl font-semibold">{error}</h2>
        <Button onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}`)} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Brand Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="channel-dashboard-page">
      {/* Header with Breadcrumb */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}`)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <span className="cursor-pointer hover:text-foreground" onClick={() => navigate('/dashboard')}>Dashboard</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-foreground flex items-center gap-1" onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}`)}>
              {brandLogo && <img src={brandLogo} alt="" className="w-4 h-4 rounded object-cover" />}
              {data?.brand_name || brandName}
            </span>
            <span>/</span>
            <span className="flex items-center gap-1">
              {platformIcon && <img src={platformIcon} alt="" className="w-4 h-4 rounded object-cover" />}
              {data?.platform_name || platformName}
            </span>
          </div>
          <h1 className="text-3xl font-bold font-heading tracking-tight flex items-center gap-2">
            {platformIcon ? (
              <img src={platformIcon} alt="" className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <Store className="w-8 h-8 text-[#3b82f6]" />
            )}
            {data?.brand_name} on {data?.platform_name}
          </h1>
          <p className="text-muted-foreground mt-1">Channel Level Dashboard</p>
        </div>
      </div>

      {/* Review history ticker — scoped to this brand + channel */}
      <ReviewTicker brand={data?.brand_name || brandName} channel={data?.platform_name || platformName} />

      {/* Meta platform - Analytics Panel + Post + Comments Thread view */}
      {platformName === 'Meta' ? (
        <div className="space-y-6" data-testid="meta-channel-view">
          {/* Industry-grade Analytics Panel (Meltwater-inspired) */}
          {metaPosts.length > 0 && (
            <Card className="card-hover overflow-hidden border-[#1877f2]/20" data-testid="meta-analytics-panel">
              <CardHeader className="pb-3 bg-gradient-to-r from-[#1877f2]/5 via-transparent to-[#bc1888]/5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-[#1877f2]" />
                      Social Listening Analytics
                      <Badge variant="secondary" className="bg-[#1877f2]/10 text-[#1877f2] text-[10px]">AI</Badge>
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      Deep analysis of {metaAnalytics?.total_comments || 0} comments across {metaPosts.length} post{metaPosts.length !== 1 ? 's' : ''} for {brandName}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchMetaAnalytics}
                      disabled={loadingMetaAnalytics}
                      data-testid="meta-analytics-refresh-btn"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loadingMetaAnalytics ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button
                      size="sm"
                      onClick={analyzeMetaComments}
                      disabled={analyzingMeta || (metaAnalytics?.unanalyzed_count === 0)}
                      className="bg-gradient-to-r from-[#1877f2] to-[#bc1888] hover:opacity-90 text-white"
                      data-testid="meta-analyze-comments-btn"
                    >
                      {analyzingMeta ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Analyzing...</>
                      ) : (
                        <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Analyze Comments{metaAnalytics?.unanalyzed_count ? ` (${metaAnalytics.unanalyzed_count})` : ''}</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6 pt-4">
                {(!metaAnalytics || metaAnalytics.analyzed_count === 0) ? (
                  <div className="py-10 text-center" data-testid="meta-analytics-empty">
                    <Brain className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                    <h4 className="text-sm font-semibold mb-1">No analyzed comments yet</h4>
                    <p className="text-xs text-muted-foreground max-w-md mx-auto">
                      Click <span className="font-medium text-foreground">Analyze Comments</span> to run Groq AI on
                      {' '}{metaAnalytics?.unanalyzed_count || 0} comments — sentiment, themes, keywords and an executive summary.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* KPI Strip */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="meta-analytics-kpis">
                      <div className="p-3 rounded-lg border border-border bg-accent/10">
                        <div className="flex items-center gap-2 mb-1">
                          <MessageSquare className="w-3.5 h-3.5 text-[#3b82f6]" />
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Analyzed</span>
                        </div>
                        <p className="text-2xl font-bold font-heading">{metaAnalytics.analyzed_count.toLocaleString()}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">of {metaAnalytics.total_comments} comments</p>
                      </div>
                      <div className="p-3 rounded-lg border border-[#2eb4ac]/30 bg-[#2eb4ac]/5">
                        <div className="flex items-center gap-2 mb-1">
                          <ThumbsUp className="w-3.5 h-3.5 text-[#2eb4ac]" />
                          <span className="text-[10px] uppercase tracking-wider text-[#2eb4ac] font-semibold">Positive</span>
                        </div>
                        <p className="text-2xl font-bold font-heading text-[#2eb4ac]">{metaAnalytics.sentiment_percent.positive}%</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{metaAnalytics.sentiment_distribution.positive} comments</p>
                      </div>
                      <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/5">
                        <div className="flex items-center gap-2 mb-1">
                          <ThumbsDown className="w-3.5 h-3.5 text-red-400" />
                          <span className="text-[10px] uppercase tracking-wider text-red-400 font-semibold">Negative</span>
                        </div>
                        <p className="text-2xl font-bold font-heading text-red-400">{metaAnalytics.sentiment_percent.negative}%</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{metaAnalytics.sentiment_distribution.negative} comments</p>
                      </div>
                      <div className="p-3 rounded-lg border border-[#f59e0b]/30 bg-[#f59e0b]/5">
                        <div className="flex items-center gap-2 mb-1">
                          <Activity className="w-3.5 h-3.5 text-[#f59e0b]" />
                          <span className="text-[10px] uppercase tracking-wider text-[#f59e0b] font-semibold">Net Sentiment</span>
                        </div>
                        <p className="text-2xl font-bold font-heading text-[#f59e0b]">
                          {metaAnalytics.sentiment_percent.positive - metaAnalytics.sentiment_percent.negative > 0 ? '+' : ''}
                          {metaAnalytics.sentiment_percent.positive - metaAnalytics.sentiment_percent.negative}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">positive - negative</p>
                      </div>
                    </div>

                    {/* AI Insight Summary */}
                    {metaAnalytics.ai_insight && (
                      <div className="p-4 rounded-lg border border-[#1877f2]/20 bg-gradient-to-br from-[#1877f2]/5 to-[#bc1888]/5" data-testid="meta-ai-insight">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1877f2] to-[#bc1888] shrink-0 flex items-center justify-center">
                            <Sparkles className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <p className="text-[10px] uppercase tracking-wider text-[#1877f2] font-semibold mb-1">AI Executive Insight</p>
                            <p className="text-sm leading-relaxed text-foreground/90">{metaAnalytics.ai_insight}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Sentiment Pie */}
                      <div className="p-4 rounded-lg border border-border bg-accent/5" data-testid="meta-sentiment-pie">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                          <Heart className="w-3.5 h-3.5" />Sentiment Distribution
                        </h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Positive', value: metaAnalytics.sentiment_distribution.positive, color: '#2eb4ac' },
                                { name: 'Neutral', value: metaAnalytics.sentiment_distribution.neutral, color: '#64748b' },
                                { name: 'Negative', value: metaAnalytics.sentiment_distribution.negative, color: '#ef4444' },
                              ].filter(d => d.value > 0)}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={85}
                              paddingAngle={2}
                            >
                              {[
                                { name: 'Positive', color: '#2eb4ac' },
                                { name: 'Neutral', color: '#64748b' },
                                { name: 'Negative', color: '#ef4444' },
                              ].map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend wrapperStyle={{ fontSize: '11px' }} iconType="circle" />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Top Themes */}
                      <div className="p-4 rounded-lg border border-border bg-accent/5" data-testid="meta-top-themes">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                          <Hash className="w-3.5 h-3.5" />Top Themes
                        </h4>
                        {metaAnalytics.top_topics.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-12">No themes detected yet</p>
                        ) : (
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={metaAnalytics.top_topics.slice(0, 8)} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                              <XAxis type="number" stroke="hsl(var(--muted-foreground))" style={{ fontSize: '10px' }} />
                              <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" style={{ fontSize: '10px' }} width={90} tickFormatter={(v) => v.length > 14 ? v.slice(0, 14) + '…' : v} />
                              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(24,119,242,0.05)' }} />
                              <Bar dataKey="count" fill="#1877f2" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    {/* Keywords + Issues */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="p-4 rounded-lg border border-border bg-accent/5" data-testid="meta-top-keywords">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                          <Lightbulb className="w-3.5 h-3.5 text-[#f59e0b]" />Top Keywords
                        </h4>
                        {metaAnalytics.top_keywords.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-6">No keywords detected</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {metaAnalytics.top_keywords.map((kw) => {
                              const max = metaAnalytics.top_keywords[0]?.count || 1;
                              const intensity = 0.5 + (kw.count / max) * 0.5;
                              return (
                                <button
                                  key={kw.name}
                                  onClick={() => navigateToReviews({ keyword: kw.name })}
                                  className="px-2.5 py-1 rounded-full border border-[#1877f2]/20 hover:border-[#1877f2] hover:bg-[#1877f2]/10 transition-all"
                                  style={{ fontSize: `${0.7 + intensity * 0.4}rem`, opacity: intensity }}
                                  data-testid={`meta-keyword-${kw.name}`}
                                >
                                  <span className="font-medium">{kw.name}</span>
                                  <span className="ml-1.5 text-muted-foreground text-[10px]">{kw.count}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className="p-4 rounded-lg border border-border bg-accent/5" data-testid="meta-top-issues">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-red-400" />Top Concerns
                        </h4>
                        {metaAnalytics.top_issues.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-6">No concerns detected — all clear</p>
                        ) : (
                          <div className="space-y-2">
                            {metaAnalytics.top_issues.slice(0, 6).map((issue) => {
                              const max = metaAnalytics.top_issues[0]?.count || 1;
                              const pct = (issue.count / max) * 100;
                              return (
                                <div key={issue.name} className="space-y-1">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="font-medium capitalize truncate">{issue.name}</span>
                                    <span className="text-muted-foreground">{issue.count}</span>
                                  </div>
                                  <div className="h-1.5 rounded-full bg-accent overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-[#f59e0b] to-red-400" style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Highlights: Top Positive vs Top Negative */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="p-4 rounded-lg border border-[#2eb4ac]/20 bg-[#2eb4ac]/5" data-testid="meta-top-positive">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-[#2eb4ac] mb-3 flex items-center gap-2">
                          <ThumbsUp className="w-3.5 h-3.5" />Loved By Audience
                        </h4>
                        {metaAnalytics.top_positive.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-4">No positive highlights yet</p>
                        ) : (
                          <div className="space-y-3">
                            {metaAnalytics.top_positive.slice(0, 3).map((c) => (
                              <div key={c.review_id} className="text-xs leading-relaxed">
                                <p className="text-foreground/90 line-clamp-3">"{c.review_text}"</p>
                                <p className="text-[10px] text-muted-foreground mt-1">— {c.author || 'Anonymous'}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="p-4 rounded-lg border border-red-500/20 bg-red-500/5" data-testid="meta-top-negative">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-red-400 mb-3 flex items-center gap-2">
                          <ThumbsDown className="w-3.5 h-3.5" />Areas To Address
                        </h4>
                        {metaAnalytics.top_negative.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-4">No negative comments — great job!</p>
                        ) : (
                          <div className="space-y-3">
                            {metaAnalytics.top_negative.slice(0, 3).map((c) => (
                              <div key={c.review_id} className="text-xs leading-relaxed">
                                <p className="text-foreground/90 line-clamp-3">"{c.review_text}"</p>
                                <p className="text-[10px] text-muted-foreground mt-1">— {c.author || 'Anonymous'}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {metaPosts.length === 0 ? (
            <Card className="card-hover">
              <CardContent className="py-12 text-center">
                <svg className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/></svg>
                <h3 className="text-lg font-semibold mb-2">No Meta Posts Tracked</h3>
                <p className="text-sm text-muted-foreground">Add Instagram/Facebook post URLs from the <button onClick={() => navigate('/settings?tab=tracking')} className="text-[#1877f2] hover:underline font-medium">Control Panel</button> to start fetching comments.</p>
              </CardContent>
            </Card>
          ) : (
            metaPosts.map((post) => (
              <Card key={post.post_id} className="card-hover overflow-hidden" data-testid={`meta-post-${post.post_id}`}>
                <div className="flex flex-col lg:flex-row">
                  {/* Left: Post Preview */}
                  <div className="lg:w-[380px] shrink-0 border-r border-border">
                    <div className="p-4 space-y-3">
                      {/* Post Header */}
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888] flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{post.label || 'Instagram Post'}</p>
                          <p className="text-[10px] text-muted-foreground">{post.brand}</p>
                        </div>
                      </div>

                      {/* Post Image/Embed */}
                      {post.image_url ? (
                        <div className="rounded-lg overflow-hidden bg-accent/20 aspect-square max-h-[300px]">
                          <img src={post.image_url} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="rounded-lg overflow-hidden bg-accent/5 border border-border">
                          <iframe
                            src={`https://www.instagram.com/p/${post.url.match(/\/(p|reel)\/([^/?]+)/)?.[2] || ''}/embed/`}
                            className="w-full h-[380px] border-0"
                            allowTransparency="true"
                            scrolling="no"
                            loading="lazy"
                          />
                        </div>
                      )}

                      {/* Caption */}
                      {post.caption && (
                        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4">{post.caption}</p>
                      )}

                      {/* Engagement Strip (views, plays, likes, comments_meta, posted_at) */}
                      {(post.view_count != null || post.like_count != null || post.play_count != null) && (
                        <div className="grid grid-cols-4 gap-2 pt-2 border-t border-border" data-testid={`meta-engagement-${post.post_id}`}>
                          <div className="text-center">
                            <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center justify-center gap-1">
                              <Eye className="w-2.5 h-2.5" />Views
                            </p>
                            <p className="text-xs font-bold tabular-nums mt-0.5">
                              {post.view_count != null ? compactNumber(post.view_count) : '—'}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center justify-center gap-1">
                              <PlayCircle className="w-2.5 h-2.5" />Plays
                            </p>
                            <p className="text-xs font-bold tabular-nums mt-0.5 text-[#1877f2]">
                              {post.play_count != null ? compactNumber(post.play_count) : '—'}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center justify-center gap-1">
                              <Heart className="w-2.5 h-2.5" />Likes
                            </p>
                            <p className="text-xs font-bold tabular-nums mt-0.5 text-[#e1306c]">
                              {post.like_count != null ? compactNumber(post.like_count) : '—'}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center justify-center gap-1">
                              <MessageSquare className="w-2.5 h-2.5" />Comments
                            </p>
                            <p className="text-xs font-bold tabular-nums mt-0.5">
                              {post.comments_count_meta != null ? compactNumber(post.comments_count_meta) : (post.total_comments != null ? compactNumber(post.total_comments) : '—')}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Posted date + Refresh stats button */}
                      <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                        {post.posted_at ? (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-2.5 h-2.5" />
                            Posted {new Date(post.posted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {post.video_duration ? <span className="ml-1.5 text-muted-foreground/70">· {Math.round(post.video_duration)}s</span> : null}
                          </span>
                        ) : <span />}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); refreshMetaPostStats(post.post_id); }}
                          disabled={refreshingStatsId === post.post_id}
                          className="inline-flex items-center gap-1 hover:text-[#1877f2] transition-colors disabled:opacity-50"
                          data-testid={`meta-refresh-stats-btn-${post.post_id}`}
                          title={post.stats_last_fetched ? `Stats refreshed ${new Date(post.stats_last_fetched).toLocaleString('en-IN')}` : 'Refresh views/likes from Instagram'}
                        >
                          <RefreshCw className={`w-2.5 h-2.5 ${refreshingStatsId === post.post_id ? 'animate-spin' : ''}`} />
                          {refreshingStatsId === post.post_id ? 'Refreshing…' : 'Refresh stats'}
                        </button>
                      </div>

                      {/* Post Stats */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1 border-t border-border flex-wrap">
                        <span data-testid={`meta-post-comments-count-${post.post_id}`}>
                          Showing <span className="font-semibold text-foreground">{(metaComments[post.post_id] || []).length}</span>
                          {post.total_comments ? <> of {post.total_comments}{(post.total_comments || 0) >= 100 ? '+' : ''}</> : null} comments
                        </span>
                        {(post.total_comments || 0) >= 100 && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#f59e0b]/10 text-[#f59e0b] border border-[#f59e0b]/20"
                            title="Apify free tier limits to 100 comments per post. Upgrade your Apify plan to fetch more."
                            data-testid={`meta-cap-badge-${post.post_id}`}
                          >
                            <AlertTriangle className="w-2.5 h-2.5" />Apify cap
                          </span>
                        )}
                        {post.last_fetched && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(post.last_fetched).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Comments Thread */}
                  <div className="flex-1 flex flex-col min-h-[400px]">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold">Comments</h3>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs" data-testid={`meta-thread-count-${post.post_id}`}>
                          Showing {(metaComments[post.post_id] || []).length}
                          {post.total_comments ? ` of ${post.total_comments}${(post.total_comments || 0) >= 100 ? '+' : ''}` : ''}
                        </Badge>
                        {(post.total_comments || 0) >= 100 && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#f59e0b]/10 text-[#f59e0b] border border-[#f59e0b]/20"
                            title="Apify free tier caps this scraper at 100 comments per post. Upgrade your Apify plan to unlock the full pagination."
                          >
                            <AlertTriangle className="w-2.5 h-2.5" />Apify cap
                          </span>
                        )}
                      </div>
                    </div>
                    <ScrollArea className="flex-1 max-h-[400px]">
                      <div className="p-4 space-y-4">
                        {(metaComments[post.post_id] || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-8">No comments fetched yet. Click "Fetch" in the Control Panel.</p>
                        ) : (
                          (metaComments[post.post_id] || []).map((comment) => (
                            <div key={comment.review_id} className="flex gap-3">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#833ab4] to-[#fd1d1d] shrink-0 flex items-center justify-center">
                                <span className="text-[9px] font-bold text-white">{(comment.author || '?')[0]?.toUpperCase()}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold">{comment.author || 'Anonymous'}</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    {comment.review_date ? new Date(comment.review_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}
                                  </span>
                                  {comment.sentiment && (
                                    <Badge variant="secondary" className={`text-[9px] px-1 py-0 ${comment.sentiment === 'positive' ? 'bg-[#2eb4ac]/10 text-[#2eb4ac]' : comment.sentiment === 'negative' ? 'bg-red-500/10 text-red-400' : 'bg-slate-500/10 text-slate-400'}`}>
                                      {comment.sentiment}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm mt-0.5 text-foreground/90">{comment.review_text}</p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      ) : platformName === 'Blinkit' ? (
        <Card className="card-hover" data-testid="sku-performance-table">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#3b82f6]" />
                  SKU Performance — {data?.platform_name}
                </CardTitle>
                <CardDescription>Manual ratings for {data?.brand_name} products on {data?.platform_name}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-center">Rating</TableHead>
                  <TableHead className="text-center">No. of Ratings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const manualSkus = [];
                  manualRatings.forEach((mr) => {
                    manualSkus.push({
                      name: mr.sku_name,
                      image_url: productImages[mr.sku_name] || '',
                      rating: mr.average_rating,
                      review_count: mr.total_rating_count,
                    });
                  });
                  skuUrls.forEach((sku) => {
                    if (!manualSkus.find(m => m.name === sku.sku_name)) {
                      manualSkus.push({
                        name: sku.sku_name,
                        image_url: productImages[sku.sku_name] || '',
                        rating: sku.last_rating,
                        review_count: sku.last_review_count,
                      });
                    }
                  });

                  if (manualSkus.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                          No ratings data. Add manual ratings in the Control Panel.
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return manualSkus.map((sku) => (
                    <TableRow key={sku.name}>
                      <TableCell className="font-medium max-w-[250px]">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded bg-accent/50 shrink-0 overflow-hidden flex items-center justify-center">
                            {sku.image_url ? (
                              <img src={sku.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <Package className="w-3 h-3 text-muted-foreground" />
                            )}
                          </div>
                          <span className="truncate">{sku.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {sku.rating != null ? (
                          <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b]">
                            <Star className="w-3 h-3 mr-1 fill-current" />{sku.rating}
                          </Badge>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        {sku.review_count != null ? (
                          <span className="font-semibold text-sm">{sku.review_count.toLocaleString()}</span>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : platformName === 'Zepto' ? (
        <Card className="card-hover" data-testid="sku-performance-table">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#3b82f6]" />
                  SKU Performance — {data?.platform_name}
                </CardTitle>
                <CardDescription>
                  Live ratings scraped from {data?.platform_name} for {data?.brand_name} products
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {lastSkuFetchedAt && (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(lastSkuFetchedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchAllSkuRatings}
                  disabled={fetchingSkuRatings || skuUrls.length === 0}
                  data-testid="qc-fetch-live-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetchingSkuRatings ? 'animate-spin' : ''}`} />
                  {fetchingSkuRatings ? 'Fetching…' : 'Fetch Live'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-center">Live Rating</TableHead>
                  <TableHead className="text-center">No. of Ratings</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {skuUrls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No tracked products. Add a Zepto URL for any SKU in the Control Panel, then click Fetch Live.
                    </TableCell>
                  </TableRow>
                ) : (
                  skuUrls.map((sku) => (
                    <TableRow key={sku.url_id}>
                      <TableCell className="font-medium max-w-[260px]">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded bg-accent/50 shrink-0 overflow-hidden flex items-center justify-center">
                            {productImages[sku.sku_name] ? (
                              <img src={productImages[sku.sku_name]} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <Package className="w-3 h-3 text-muted-foreground" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate">{sku.sku_name}</div>
                            {sku.url && (
                              <a href={sku.url} target="_blank" rel="noreferrer" className="text-[10px] text-muted-foreground hover:text-[#3b82f6] inline-flex items-center gap-1">
                                <ExternalLink className="w-2.5 h-2.5" />View on Zepto
                              </a>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {sku.last_rating != null ? (
                          <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b]">
                            <Star className="w-3 h-3 mr-1 fill-current" />{sku.last_rating}
                          </Badge>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {sku.last_review_count != null ? (
                          <span className="font-semibold text-sm">{Number(sku.last_review_count).toLocaleString()}</span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {sku.last_rating != null ? (
                          <Badge variant="secondary" className="bg-[#2eb4ac]/10 text-[#2eb4ac] text-[10px]">Live</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-amber-500/10 text-amber-400 text-[10px]">Pending</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
      <>
      {/* Row 1: KPIs with Info Icons - No Health Score */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard
          metricKey="total_reviews"
          icon={MessageSquare}
          label="Analysed Reviews"
          value={data?.total_reviews?.toLocaleString()}
          subtext="On this channel"
        />
        <MetricCard
          metricKey="positive"
          icon={TrendingUp}
          iconColor="text-[#2eb4ac]"
          label="Positive"
          value={positivePercent}
          suffix="%"
          subtext={`${positiveCount} reviews`}
          onClick={() => navigateToReviews({ sentiment: 'positive' })}
          highlight={true}
          highlightColor="#2eb4ac"
        />
        <MetricCard
          metricKey="negative"
          icon={TrendingDown}
          iconColor="text-destructive"
          label="Negative"
          value={negativePercent}
          suffix="%"
          subtext={`${negativeCount} reviews`}
          onClick={() => navigateToReviews({ sentiment: 'negative' })}
        />
        <MetricCard
          metricKey="total_skus"
          icon={Package}
          iconColor="text-[#8b5cf6]"
          label="SKUs"
          value={data?.total_skus}
          subtext="Products"
        />
      </div>

      {/* AI Summary - 1-2 liner review summary */}
      {data?.ai_summary && (
        <Card className="card-hover bg-gradient-to-r from-[#3b82f6]/5 via-[#2eb4ac]/5 to-transparent border-l-4 border-l-[#3b82f6]" data-testid="ai-summary">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#3b82f6]/10 flex items-center justify-center flex-shrink-0">
                <Brain className="w-5 h-5 text-[#3b82f6]" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-[#3b82f6] mb-1 uppercase tracking-wide">AI Insight</p>
                <p className="text-sm text-foreground">
                  {data?.total_reviews > 0 
                    ? `${data?.brand_name} on ${data?.platform_name}: ${data?.total_reviews?.toLocaleString()} reviews, ${positivePercent}% positive, ${data?.avg_rating}/5 avg rating. ${data?.total_skus} SKUs tracked. "${data?.top_topics?.[0]?.topic || 'product quality'}" most mentioned (${data?.top_topics?.[0]?.count || 0}x).`
                    : 'Upload reviews to get AI-powered channel insights.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unified SKU Performance Table */}
      <Card className="card-hover" data-testid="sku-performance-table">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Package className="w-4 h-4 text-[#3b82f6]" />
                SKU Performance
              </CardTitle>
              <CardDescription>Unified view of analysed and live data for all SKUs on {data?.platform_name}</CardDescription>
            </div>
            {skuUrls.length > 0 && (
              <div className="flex items-center gap-3">
                {lastSkuFetchedAt && (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(lastSkuFetchedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                <ColumnConfigPopover columns={channelSkuCols} onChange={setChannelSkuCols} />
                <button
                  onClick={fetchAllSkuRatings}
                  disabled={fetchingSkuRatings}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#2eb4ac]/10 text-[#2eb4ac] text-sm font-medium hover:bg-[#2eb4ac]/20 disabled:opacity-50 transition-all"
                  data-testid="fetch-all-sku-ratings-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${fetchingSkuRatings ? 'animate-spin' : ''}`} />
                  {fetchingSkuRatings ? 'Fetching...' : 'Fetch All Live Ratings'}
                </button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Unified Table */}
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  {visChannelSkuCols.map(col => (
                    <TableHead key={col.id} className={col.id === 'product' ? '' : 'text-center'}>{col.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const skuMap = new Map();

                  sortedSkus.forEach((sku) => {
                    const key = sku.product_name.toLowerCase().trim();
                    skuMap.set(key, {
                      name: sku.product_name,
                      image_url: sku.image_url || '',
                      analysed_reviews: sku.total_reviews,
                      analysed_rating: sku.avg_rating,
                      sentiment_score: sku.sentiment_score,
                      negative_count: sku.negative_count || 0,
                      positive_count: sku.positive_count || 0,
                      live_rating: null,
                      live_reviews: null,
                      url_id: null,
                      url: null,
                      last_scraped: null,
                    });
                  });

                  skuUrls.forEach((sku) => {
                    const key = sku.sku_name.toLowerCase().trim();
                    if (skuMap.has(key)) {
                      const existing = skuMap.get(key);
                      existing.live_rating = sku.last_rating;
                      existing.live_reviews = sku.last_review_count;
                      existing.url_id = sku.url_id;
                      existing.url = sku.url;
                      existing.last_scraped = sku.last_scraped;
                    } else {
                      skuMap.set(key, {
                        name: sku.sku_name,
                        image_url: productImages[sku.sku_name] || '',
                        analysed_reviews: null,
                        analysed_rating: null,
                        sentiment_score: null,
                        negative_count: 0,
                        positive_count: 0,
                        live_rating: sku.last_rating,
                        live_reviews: sku.last_review_count,
                        url_id: sku.url_id,
                        url: sku.url,
                        last_scraped: sku.last_scraped,
                      });
                    }
                  });

                  const merged = Array.from(skuMap.values());

                  if (merged.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={visChannelSkuCols.length} className="text-center py-8 text-muted-foreground">
                          No SKU data available. Upload reviews or add tracking URLs from Settings.
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return merged.map((sku) => (
                    <TableRow
                      key={sku.name}
                      className="cursor-pointer hover:bg-accent/50 group"
                      onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(platformName)}/product/${encodeURIComponent(sku.name)}`)}
                    >
                      {visChannelSkuCols.map(col => {
                        if (col.id === 'product') return (
                          <TableCell key={col.id} className="font-medium max-w-[220px]">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded bg-accent/50 shrink-0 overflow-hidden flex items-center justify-center">
                                {sku.image_url ? (
                                  <img src={sku.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                ) : (
                                  <Package className="w-3 h-3 text-muted-foreground" />
                                )}
                              </div>
                            {sku.url ? (
                              <a href={sku.url} target="_blank" rel="noopener noreferrer" className="text-[#2eb4ac] hover:underline flex items-center gap-1 truncate" onClick={(e) => e.stopPropagation()}>
                                <span className="truncate">{sku.name}</span>
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              </a>
                            ) : (
                              <span className="truncate block">{sku.name}</span>
                            )}
                            </div>
                          </TableCell>
                        );
                        if (col.id === 'live_rating') return (
                          <TableCell key={col.id} className="text-center">
                            {sku.live_rating != null ? (
                              <Badge variant="secondary" className="bg-[#2eb4ac]/10 text-[#2eb4ac]">
                                <Star className="w-3 h-3 mr-1 fill-current" />{sku.live_rating}
                              </Badge>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                        );
                        if (col.id === 'live_reviews') return (
                          <TableCell key={col.id} className="text-center">
                            {sku.live_reviews != null ? (
                              <span className="font-semibold text-sm">{sku.live_reviews.toLocaleString()}</span>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                        );
                        if (col.id === 'analysed_reviews') return (
                          <TableCell key={col.id} className="text-center">
                            {sku.analysed_reviews != null ? (
                              <span className="text-sm">{sku.analysed_reviews.toLocaleString()}</span>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                        );
                        if (col.id === 'analysed_rating') return (
                          <TableCell key={col.id} className="text-center">
                            {sku.analysed_rating != null ? (
                              <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b]">
                                <Star className="w-3 h-3 mr-1 fill-current" />{sku.analysed_rating}
                              </Badge>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                        );
                        if (col.id === 'sentiment') return (
                          <TableCell key={col.id} className="text-center">
                            {sku.analysed_reviews != null ? (
                              <div className="flex items-center justify-center gap-3">
                                <span className="text-xs text-red-400 font-medium" title="Negative">{sku.negative_count}<span className="text-muted-foreground/60 ml-0.5">neg</span></span>
                                <span className="text-xs text-slate-400 font-medium" title="Neutral">{sku.analysed_reviews - sku.positive_count - sku.negative_count}<span className="text-muted-foreground/60 ml-0.5">neu</span></span>
                              </div>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                        );
                        return null;
                      })}
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>


      {/* Row 4: Top & Underperforming SKUs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing SKUs */}
        <Card className="card-hover" data-testid="top-skus">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#f59e0b]" />
              🏆 Top Performing SKUs
            </CardTitle>
            <CardDescription>Click to view product dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rankedTopSkus.map((sku, idx) => (
                <button
                  key={sku.product_name}
                  onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(platformName)}/product/${encodeURIComponent(sku.product_name)}`)}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-accent/30 hover:bg-accent/50 transition-all cursor-pointer group text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xl">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '📦'}</span>
                    <div className="w-7 h-7 rounded bg-accent/50 shrink-0 overflow-hidden flex items-center justify-center">
                      {sku.image_url ? <img src={sku.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : <Package className="w-3 h-3 text-muted-foreground" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate max-w-[180px]">{sku.product_name}</p>
                      <p className="text-xs text-muted-foreground">{sku.total_reviews} reviews</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sku.avg_rating > 0 ? (
                      <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b]">
                        <Star className="w-3 h-3 mr-1 fill-current" />{sku.avg_rating}
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/60 italic" data-testid={`not-live-yet-top-${sku.product_name}`}>Not Live Yet</span>
                    )}
                    <Badge variant="secondary" className="bg-[#2eb4ac]/10 text-[#2eb4ac]">{sku.sentiment_score}%</Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
              {!rankedTopSkus.length && <p className="text-sm text-muted-foreground text-center py-4">No data available</p>}
            </div>
          </CardContent>
        </Card>

        {/* Underperforming SKUs - with sentiment % */}
        <Card className="card-hover" data-testid="underperforming-skus">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-destructive" />
              ⚠️ Underperforming SKUs
            </CardTitle>
            <CardDescription>Click to view product dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rankedUnderperformingSkus.map((sku) => (
                <button
                  key={sku.product_name}
                  onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(platformName)}/product/${encodeURIComponent(sku.product_name)}`)}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-destructive/5 hover:bg-destructive/10 transition-all cursor-pointer group text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xl">⚠️</span>
                    <div className="w-7 h-7 rounded bg-accent/50 shrink-0 overflow-hidden flex items-center justify-center">
                      {sku.image_url ? <img src={sku.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : <Package className="w-3 h-3 text-muted-foreground" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate max-w-[180px]">{sku.product_name}</p>
                      <p className="text-xs text-muted-foreground">{sku.total_reviews} reviews • {sku.negative_count} negative</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sku.avg_rating > 0 ? (
                      <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b]">
                        <Star className="w-3 h-3 mr-1 fill-current" />{sku.avg_rating}
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/60 italic" data-testid={`not-live-yet-under-${sku.product_name}`}>Not Live Yet</span>
                    )}
                    <Badge variant="secondary" className={`${sku.sentiment_score >= 40 ? 'bg-[#f59e0b]/10 text-[#f59e0b]' : 'bg-destructive/10 text-destructive'}`}>
                      {sku.sentiment_score || 0}%
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
              {!rankedUnderperformingSkus.length && <p className="text-sm text-muted-foreground text-center py-4">No underperforming SKUs</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 5: Rating Distribution */}
      <Card className="card-hover" data-testid="rating-distribution">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Star className="w-4 h-4 text-[#f59e0b]" />
            ⭐ Rating Distribution
          </CardTitle>
          <CardDescription>Breakdown of 1★–5★ reviews</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            {data?.rating_distribution?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.rating_distribution} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <YAxis type="category" dataKey="rating" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `${v}★`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Reviews" radius={[0, 4, 4, 0]}>
                    {data.rating_distribution.map((entry) => (
                      <Cell key={entry.rating} fill={RATING_COLORS[entry.rating] || '#94a3b8'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No rating data</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Row 6: Topics - Clickable */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Positive Topics */}
        <Card className="card-hover" data-testid="positive-topics">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Heart className="w-4 h-4 text-[#2eb4ac]" />
              ❤️ Top Positive Topics
            </CardTitle>
            <CardDescription>Click to see reviews for each topic</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data?.positive_topics?.length > 0 ? (
                data.positive_topics.map((item) => (
                  <button
                    key={item.topic}
                    onClick={() => navigateToReviews({ topic: item.topic, sentiment: 'positive' })}
                    className="px-3 py-1.5 rounded-full bg-[#2eb4ac]/10 text-[#2eb4ac] text-sm font-medium hover:bg-[#2eb4ac]/20 hover:scale-105 transition-all cursor-pointer"
                  >
                    {item.topic} <span className="opacity-60">({item.count})</span>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No positive topics found</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Negative Topics */}
        <Card className="card-hover" data-testid="negative-topics">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              ⚠️ Top Negative Topics
            </CardTitle>
            <CardDescription>Click to see reviews for each topic</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data?.negative_topics?.length > 0 ? (
                data.negative_topics.map((item) => (
                  <button
                    key={item.topic}
                    onClick={() => navigateToReviews({ topic: item.topic, sentiment: 'negative' })}
                    className="px-3 py-1.5 rounded-full bg-destructive/10 text-destructive text-sm font-medium hover:bg-destructive/20 hover:scale-105 transition-all cursor-pointer"
                  >
                    {item.topic} <span className="opacity-60">({item.count})</span>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No negative topics found</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 7: Platform Issues & Emerging Complaints */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform-Specific Issues */}
        <Card className="card-hover" data-testid="platform-issues">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Truck className="w-4 h-4 text-[#f97316]" />
              📦 Platform-Specific Issues
            </CardTitle>
            <CardDescription>Delivery, packaging, or seller complaints</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data?.platform_specific_issues?.length > 0 ? (
                data.platform_specific_issues.map((item) => (
                  <button
                    key={item.issue}
                    onClick={() => navigateToReviews({ highlight: item.issue })}
                    className="w-full flex items-center justify-between p-2 rounded-lg bg-[#f97316]/5 hover:bg-[#f97316]/10 transition-all cursor-pointer text-left"
                  >
                    <span className="text-sm font-medium capitalize">{item.issue}</span>
                    <Badge variant="secondary" className="bg-[#f97316]/10 text-[#f97316]">{item.count}</Badge>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No platform-specific issues detected 🎉</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Emerging Complaint Trends */}
        <Card className="card-hover" data-testid="emerging-complaints">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Search className="w-4 h-4 text-[#8b5cf6]" />
              🔍 Emerging Complaint Trends
            </CardTitle>
            <CardDescription>Issues increasing recently</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.emerging_complaints?.length > 0 ? (
                data.emerging_complaints.map((item) => (
                  <button
                    key={item.issue}
                    onClick={() => navigateToReviews({ highlight: item.issue, sentiment: 'negative' })}
                    className="w-full flex items-center justify-between p-2 rounded-lg bg-accent/30 hover:bg-accent/50 transition-all cursor-pointer text-left"
                  >
                    <span className="text-sm font-medium capitalize">{item.issue}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{item.recent} recent</Badge>
                      <span className={`text-xs font-semibold ${item.change > 0 ? 'text-destructive' : 'text-[#2eb4ac]'}`}>
                        {item.change > 0 ? `↑${item.change}` : item.change === 0 ? '→' : `↓${Math.abs(item.change)}`}
                      </span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No emerging trends detected</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 8: Recent Critical Reviews */}
      <Card className="card-hover" data-testid="critical-reviews">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Clock className="w-4 h-4 text-destructive" />
            📝 Recent Critical Reviews (1★-2★)
          </CardTitle>
          <CardDescription>Latest reviews needing attention</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[250px]">
            <div className="space-y-3 pr-2">
              {data?.critical_reviews?.length > 0 ? (
                data.critical_reviews.map((review) => (
                  <div key={review.review_id} className="p-3 rounded-lg bg-destructive/5 border-l-4 border-destructive">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {Array(5).fill(0).map((_, i) => (
                            <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'text-[#f59e0b] fill-[#f59e0b]' : 'text-muted'}`} />
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/brand/${encodeURIComponent(brandName)}/channel/${encodeURIComponent(platformName)}/product/${encodeURIComponent(review.product_name)}`)}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                      >
                        <span className="max-w-[150px] truncate">{review.product_name}</span>
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <ExpandableText text={review.review_text} maxLength={150} />
                    </p>
                    {review.detected_issues?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {review.detected_issues.slice(0, 3).map((issue) => (
                          <button
                            key={issue}
                            onClick={() => navigateToReviews({ highlight: issue })}
                            className="px-2 py-0.5 rounded text-xs bg-destructive/10 text-destructive hover:bg-destructive/20 cursor-pointer"
                          >
                            {issue}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No critical reviews - Great! 🎉</p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
